import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";

type DrawerContextType = {
  showDrawer: (id: string, keepPrevious?: boolean) => void;
  closeDrawer: () => void;
  isOpen: (id: string) => boolean;
  isPrevious: (id: string) => boolean;
  restorePreviousDrawer?: () => void;
  previousAvailable?: boolean;
  drawerId: string | undefined;
};

const DrawerContext = createContext<DrawerContextType>({
  showDrawer: () => {},
  closeDrawer: () => {},
  isOpen: () => false,
  isPrevious: () => false,
  drawerId: undefined,
});

export const DrawerContextProvider = ({ children }: PropsWithChildren) => {
  const [drawers, setDrawers] = useState<string[]>([]);

  const showDrawer = useCallback((id: string, keepPrevious = false) => {
    if (keepPrevious) {
      setDrawers((prev) => [id, ...prev]);
    } else {
      setDrawers([id]);
    }
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawers([]);
  }, []);

  const restorePreviousDrawer = useCallback(() => {
    setDrawers((prev) => prev.slice(1));
  }, []);

  return (
    <DrawerContext.Provider
      value={{
        showDrawer,
        closeDrawer,
        isOpen: (id) => drawers.includes(id),
        isPrevious: (id) => drawers.includes(id) && drawers[0] !== id,
        restorePreviousDrawer,
        previousAvailable: drawers.length > 1,
        drawerId: drawers[0],
      }}
    >
      {children}
    </DrawerContext.Provider>
  );
};

export const useDrawerContext = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error(
      "useDrawerContext must be used within a DrawerContextProvider"
    );
  }
  return context;
};
